import React, { useState, useEffect, useCallback, Suspense } from 'react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useToast } from './hooks/useToast';
import { getClientBySlug } from './utils/auth';
import { 
  DashboardErrorBoundary, 
  AdminErrorBoundary, 
  AuthErrorBoundary 
} from './components/ErrorBoundaries';
import ToastContainer from './components/ui/ToastContainer';
import LoadingSpinner from './components/ui/LoadingSpinner';
import SkipLinks from './components/ui/SkipLinks';
import './styles/accessibility.css';

// Lazy load components for better performance
const ClientDashboard = React.lazy(() => import('./components/ClientDashboard'));
const AdminDashboard = React.lazy(() => import('./components/admin/AdminDashboard'));
const ClientLogin = React.lazy(() => import('./components/auth/ClientLogin'));
const AdminLogin = React.lazy(() => import('./components/auth/AdminLogin'));
const ClientAccountLogin = React.lazy(() => import('./components/auth/ClientAccountLogin'));
const HistoricalDashboard = React.lazy(() => import('./components/HistoricalDashboard'));
const EvolutionPage = React.lazy(() => import('./components/EvolutionPage'));
const HomePage = React.lazy(() => import('./components/HomePage'));

// Loading component for lazy-loaded routes
const RouteLoadingSpinner = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
    <LoadingSpinner size="xl" text="Loading..." />
  </div>
);

// Route parser to determine what to render
const AppRouter: React.FC = () => {
  const { authState } = useAuth();
  const { toasts, dismissToast } = useToast();
  const [routeInfo, setRouteInfo] = useState<{
    type: 'home' | 'admin' | 'admin-all-data' | 'client' | 'historical-dashboard';
    clientSlug?: string;
    clientId?: string;
    versionId?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const parseRoute = useCallback(async () => {
    const path = window.location.pathname;
    
    if (path === '/' || path === '') {
      // Home page - show landing page
      setRouteInfo({ type: 'home' });
    } else if (path === '/admin') {
      setRouteInfo({ type: 'admin' });
    } else if (path.startsWith('/dashboard/') && path.includes('/version/')) {
      // Historical dashboard route: /dashboard/{clientSlug}/version/{versionId}
      const parts = path.split('/');
      if (parts.length === 5 && parts[1] === 'dashboard' && parts[3] === 'version') {
        const clientSlug = parts[2];
        const versionId = parts[4];
        
        try {
          const client = await getClientBySlug(clientSlug);
          if (client) {
            setRouteInfo({ 
              type: 'historical-dashboard', 
              clientSlug, 
              clientId: client.id,
              versionId 
            });
          } else {
            setRouteInfo({ type: 'home' });
          }
                } catch (error) {
        setRouteInfo({ type: 'home' });
      }
      } else {
        setRouteInfo({ type: 'home' });
      }
    } else {
      // Extract client slug from URL
      const slug = path.substring(1); // Remove leading /
      
      // Check if client exists
      try {
        const client = await getClientBySlug(slug);
        if (client) {
          setRouteInfo({ 
            type: 'client', 
            clientSlug: slug, 
            clientId: client.id 
          });
        } else {
          // Client not found, show home page
          setRouteInfo({ type: 'home' });
        }
      } catch (error) {
        setRouteInfo({ type: 'home' });
      }
    }
    
    setLoading(false);
  }, [getClientBySlug]);

  useEffect(() => {
    parseRoute();
  }, [parseRoute]);

  // Show loading while determining route
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!routeInfo) {
    return (
      <>
        <HomePage />
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Home page
  if (routeInfo.type === 'home') {
    return (
      <>
        <HomePage />
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Admin routes
  if (routeInfo.type === 'admin') {
    if (!authState.isAuthenticated || authState.userType !== 'admin') {
      return (
        <>
          <AuthErrorBoundary>
            <Suspense fallback={<RouteLoadingSpinner />}>
              <AdminLogin />
            </Suspense>
          </AuthErrorBoundary>
          <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        </>
      );
    }
    return (
      <>
        <AdminErrorBoundary>
          <Suspense fallback={<RouteLoadingSpinner />}>
            <AdminDashboard />
          </Suspense>
        </AdminErrorBoundary>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }



  // Historical dashboard route - PROTECTED with same access rules
  if (routeInfo.type === 'historical-dashboard' && routeInfo.clientSlug && routeInfo.clientId && routeInfo.versionId) {
    // Check if user is authenticated and has access to THIS SPECIFIC client
    const hasAccess = authState.isAuthenticated && (
      // Admin has access to all clients
      authState.userType === 'admin' || 
      // Original client owner has access to their own client
      (authState.userType === 'client' && authState.user?.id === routeInfo.clientId) ||
      // Viewer/Editor must be associated with THIS specific client
      ((authState.userType === 'viewer' || authState.userType === 'editor') && 
       (authState.user as any)?.client_id === routeInfo.clientId)
    );

    if (!hasAccess) {
      return (
        <>
          <AuthErrorBoundary>
            <Suspense fallback={<RouteLoadingSpinner />}>
              <ClientAccountLogin clientSlug={routeInfo.clientSlug} />
            </Suspense>
          </AuthErrorBoundary>
          <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        </>
      );
    }

    return (
      <>
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingSpinner />}>
            <HistoricalDashboard />
          </Suspense>
        </DashboardErrorBoundary>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Client routes - Now PROTECTED, requires viewer/editor authentication
  if (routeInfo.type === 'client' && routeInfo.clientSlug && routeInfo.clientId) {
    // Check if user is authenticated and has access to THIS SPECIFIC client
    const hasAccess = authState.isAuthenticated && (
      // Admin has access to all clients
      authState.userType === 'admin' || 
      // Original client owner has access to their own client
      (authState.userType === 'client' && authState.user?.id === routeInfo.clientId) ||
      // Viewer/Editor must be associated with THIS specific client
      ((authState.userType === 'viewer' || authState.userType === 'editor') && 
       (authState.user as any)?.client_id === routeInfo.clientId)
    );



    if (!hasAccess) {
      return (
        <>
          <AuthErrorBoundary>
            <Suspense fallback={<RouteLoadingSpinner />}>
              <ClientAccountLogin clientSlug={routeInfo.clientSlug} />
            </Suspense>
          </AuthErrorBoundary>
          <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        </>
      );
    }

    return (
      <>
        <DashboardErrorBoundary>
          <Suspense fallback={<RouteLoadingSpinner />}>
            <ClientDashboard clientSlug={routeInfo.clientSlug} clientId={routeInfo.clientId} />
          </Suspense>
        </DashboardErrorBoundary>
        <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      </>
    );
  }

  // Fallback
  return (
    <>
      <Suspense fallback={<RouteLoadingSpinner />}>
        <HomePage />
      </Suspense>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <SkipLinks />
        <AppRouter />
      </div>
    </AuthProvider>
  );
}

export default App;